import React from 'react';
import {
  Box,
  Button,
  Flex,
  Icon,
  Link,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FaCookieBite } from 'react-icons/fa';

export type CookieConsentBannerProps = {
  onAccept: () => void;
  /** URL pública de política de privacidad (opcional). */
  privacyPolicyUrl?: string;
};

/**
 * Barra compacta inferior (RGPD/ePrivacy); el detalle completo se abre en modal.
 */
const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({ onAccept, privacyPolicyUrl }) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const shellBg = useColorModeValue(
    'linear-gradient(145deg, rgba(255,255,255,0.94) 0%, rgba(248,250,252,0.90) 100%)',
    'linear-gradient(145deg, rgba(8,15,28,0.94) 0%, rgba(15,23,42,0.90) 100%)'
  );
  const borderColor = useColorModeValue('rgba(148,163,184,0.28)', 'rgba(148,163,184,0.20)');
  const titleColor = useColorModeValue('gray.800', 'white');
  const stripTextColor = useColorModeValue('gray.600', 'gray.300');
  const iconColor = useColorModeValue('orange.500', 'orange.300');
  const shadow = useColorModeValue(
    '0 16px 38px rgba(15, 23, 42, 0.12), inset 0 1px 0 rgba(255,255,255,0.85)',
    '0 18px 44px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.06)'
  );
  const linkColor = useColorModeValue('blue.600', 'cyan.300');
  const listColor = useColorModeValue('gray.700', 'gray.200');
  const modalBg = useColorModeValue('white', 'gray.800');

  const bulletKeys = [
    'orgcloudCookieConsentItem1',
    'orgcloudCookieConsentItem2',
    'orgcloudCookieConsentItem3',
    'orgcloudCookieConsentItem4',
    'orgcloudCookieConsentItem5',
  ] as const;

  const handleAccept = () => {
    onAccept();
    onClose();
  };

  return (
    <>
      <Box
        role="region"
        aria-label={t('orgcloudCookieConsentTitle', { defaultValue: 'How OrgCloud uses cookies' })}
        position="fixed"
        bottom={{ base: 'calc(8px + env(safe-area-inset-bottom, 0px))', md: 4 }}
        left={{ base: 0, md: 6 }}
        right={{ base: 0, md: 'auto' }}
        zIndex={1150}
        px={{ base: 3, md: 4 }}
        pb={0}
        pt={0}
        pointerEvents="none"
        display="flex"
        justifyContent={{ base: 'center', md: 'flex-start' }}
      >
        <Box
          pointerEvents="auto"
          w="100%"
          maxW={{ base: 'calc(100vw - 24px)', md: '430px' }}
          py={{ base: 2, md: 2 }}
          px={{ base: 2.5, md: 2.5 }}
          borderRadius={{ base: '18px', md: '18px' }}
          borderWidth="1px"
          borderColor={borderColor}
          bg={shellBg}
          backdropFilter="saturate(180%) blur(16px)"
          sx={{
            WebkitBackdropFilter: 'saturate(180%) blur(16px)',
            boxShadow: shadow,
          }}
        >
          <Flex
            direction={{ base: 'column', md: 'row' }}
            align={{ base: 'stretch', sm: 'center' }}
            gap={{ base: 2, md: 2 }}
          >
            <Flex align="center" gap={2.5} flex={1} minW={0}>
              <Icon
                as={FaCookieBite}
                boxSize={3}
                color={iconColor}
                flexShrink={0}
                aria-hidden
              />
              <Text
                id="orgcloud-cookie-consent-desc"
                fontSize="xs"
                color={stripTextColor}
                lineHeight="1.3"
                noOfLines={{ base: 2, sm: 1 }}
              >
                {t('orgcloudCookieConsentStrip', {
                  defaultValue:
                    'Usamos cookies esenciales para seguridad, sesión y preferencias.',
                })}
              </Text>
            </Flex>

            <Flex
              flexShrink={0}
              gap={1.5}
              flexWrap="wrap"
              justify={{ base: 'stretch', sm: 'flex-end' }}
              w={{ base: '100%', sm: 'auto' }}
            >
              <Button
                variant="ghost"
                size="sm"
                minH={{ base: '34px', sm: '32px' }}
                px={3}
                fontWeight="700"
                colorScheme="cyan"
                flex={{ base: 1, sm: 'initial' }}
                onClick={onOpen}
              >
                {t('orgcloudCookieConsentMore', { defaultValue: 'Learn more' })}
              </Button>
              <Button
                size="sm"
                minH={{ base: '34px', sm: '32px' }}
                minW={{ base: '44px', sm: 'auto' }}
                px={3}
                colorScheme="brand"
                fontWeight="800"
                flex={{ base: 1, sm: 'initial' }}
                boxShadow="0 12px 28px rgba(67, 56, 202, 0.24)"
                onClick={() => onAccept()}
              >
                {t('orgcloudCookieConsentAccept', { defaultValue: 'Accept' })}
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Box>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        scrollBehavior="inside"
        isCentered
        motionPreset="none"
      >
        <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(6px)" />
        <ModalContent
          mx={3}
          my={4}
          bg={modalBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="xl"
          boxShadow="xl"
        >
          <ModalHeader id="orgcloud-cookie-consent-title" color={titleColor} pr={12} fontSize={{ base: 'md', md: 'lg' }}>
            {t('orgcloudCookieConsentTitle', { defaultValue: 'How OrgCloud uses cookies' })}
          </ModalHeader>
          <ModalCloseButton
            aria-label={t('orgcloudCookieConsentCloseModal', { defaultValue: 'Close cookie information' })}
          />

          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm" color={stripTextColor} lineHeight="tall">
                {t('orgcloudCookieConsentLead', {
                  defaultValue:
                    'OrgCloud uses cookies and similar technologies to deliver the service securely, keep you signed in, remember your language and branding preferences, and provide essential product guidance.',
                })}
              </Text>
              <List spacing={2} pl={0} styleType="none" fontSize="sm" color={listColor}>
                {bulletKeys.map((key) => (
                  <ListItem
                    key={key}
                    position="relative"
                    pl={4}
                    _before={{
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '0.65em',
                      w: '5px',
                      h: '5px',
                      borderRadius: 'full',
                      bg: linkColor,
                    }}
                  >
                    {t(key, { defaultValue: '' })}
                  </ListItem>
                ))}
              </List>
              {privacyPolicyUrl ? (
                <Link
                  href={privacyPolicyUrl}
                  isExternal
                  fontSize="sm"
                  fontWeight="600"
                  color={linkColor}
                  textDecoration="underline"
                  _hover={{ opacity: 0.9 }}
                >
                  {t('orgcloudCookieConsentPrivacyLink', { defaultValue: 'Privacy policy' })}
                </Link>
              ) : null}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="cyan"
              fontWeight="800"
              minH="44px"
              w={{ base: '100%', sm: 'auto' }}
              ml={{ base: 0, sm: 'auto' }}
              onClick={handleAccept}
            >
              {t('orgcloudCookieConsentAccept', { defaultValue: 'Accept' })}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CookieConsentBanner;
